/**
 * MessageInput — the chat composer (restyled for the design revamp).
 *
 * Design (`docs/GardEn/handoff-2026-06/garden/screens-chat.jsx`): two standalone
 * icon buttons (upload + camera) followed by a pill text field with the round
 * send button nested at its right edge. The button fills with the primary
 * colour once there is text and greys out when empty.
 *
 * Behaviour (updated for chat document attachments):
 *   - Auto-growing `<textarea>` (caps at a max height, then scrolls).
 *   - Enter sends; Shift+Enter inserts a newline.
 *   - Disabled while streaming; a Stop button replaces Send mid-stream (AC #7).
 *   - Clip button opens a hidden multi-file picker (accept=ACCEPTED_TYPES, ≤5).
 *   - Camera button opens a file picker with capture="environment".
 *   - Each selected file goes through `useAttachmentUpload.add`.
 *   - `react-dropzone` wraps the composer for drag-drop + paste (progressive).
 *   - Chips render above the input row (AttachmentChips).
 *   - Send is enabled when there's text OR ≥1 ready attachment.
 *   - `onSend(message, attachmentUploadIds)` — Phase 6 threads the ids into the
 *     request body; on send the drafts are cleared.
 *   - All strings come from the `chat` i18n namespace (RU/KY/EN first-class).
 *   - ≥44px tap targets (AC #15). Tokens via the `--g-*` mirror.
 *
 * THE DESIGN COMPOSER (2026-08, assistant-surface Task 10). Inside the design
 * frame (`useAssistantDesign` — ≥1200px, light theme) the composition above is
 * replaced by the design's single row (spec §4.7). BEHAVIOUR is not: Enter
 * sends, Shift+Enter newlines, Stop replaces Send mid-stream, the picker opens
 * synchronously from the control's own click, and every testid is the same.
 * Below the frame this file renders exactly what it always has.
 */
import { Icon } from "@garden/ui";
import { Lock, Plus } from "lucide-react";

import { VoiceRecordingStrip } from "@/features/chat/components/VoiceRecordingStrip";
import { useTranscribe, type UseTranscribeGuestOptions } from "@/features/chat/useTranscribe";
import {
  isRecordingSupported,
  useVoiceRecorder,
} from "@/features/chat/useVoiceRecorder";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import {
  ACCEPTED_TYPES,
  MAX_ATTACHMENTS,
  type AttachmentDraft,
  type MessageAttachment,
} from "@/features/chat/attachment-types";
import { AttachmentChips } from "@/features/chat/components/AttachmentChips";
import { readGuestDraft, writeGuestDraft } from "@/features/chat/guest/pointer";
import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import { useAttachmentUpload } from "@/features/chat/useAttachmentUpload";
import { StopButton } from "@/features/chat/components/StopButton";
import { iconProps } from "@/lib/icons";

const MAX_TEXTAREA_HEIGHT_PX = 140;

/**
 * Map a ready in-flight `AttachmentDraft` to a `MessageAttachment`-shaped
 * display object (snake_case wire shape) so ONE shape renders both live
 * (just-sent) and rehydrated. The persisted server fields (`file_url`,
 * `doc_type`, `page_count`) are unknown client-side at send time — they fill
 * in on a thread reload — so the live display falls back to the local thumb /
 * file-type icon (`file_url: null`).
 */
function draftToDisplay(draft: AttachmentDraft): MessageAttachment {
  return {
    id: draft.localId,
    kind: draft.kind,
    content_type: draft.file.type,
    filename: draft.file.name,
    doc_type: null,
    page_count: null,
    thumb_url: draft.thumbDataUrl,
    file_url: null,
  };
}

/**
 * Imperative surface for siblings that act on the composer.
 *
 * `prefill` and `prependLeadIn` put text in the composer: the draft restored
 * after a sign-in, the text a refused send hands back, and the guest's paste
 * path. `openFilePicker` opens the composer's own upload flow. The hidden
 * `<input type="file">` and the draft list both live in this component
 * (`useAttachmentUpload` is local state), so the picker cannot be opened from
 * outside any other way.
 *
 * A caller must invoke `openFilePicker` synchronously from its click handler,
 * which keeps it inside the browser's transient user activation; a
 * `useEffect`-driven signal would risk the picker being suppressed. Its caller
 * is the empty-state «Разобрать мою смету» row (SCRUM-146): the starter list
 * the owner removed on 2026-10-08 is back, exactly as before, since 2026-10-09
 * (ADR-053). For a guest with attachments locked that row writes the paste
 * lead-in through `prependLeadIn` instead. Also covered by
 * `MessageInput-guest.test.tsx`.
 */
export interface MessageInputHandle {
  /** Open the multi-file picker, exactly as the paperclip button does (a
   * locked guest gets the sign-up gate instead). */
  openFilePicker: () => void;
  /**
   * Put `text` in the composer, focus it, caret at the end. The restored
   * draft and the text a refused send hands back use it; nothing is sent.
   */
  prefill: (text: string) => void;
  /**
   * Put `lead` in FRONT of whatever is typed, focus the composer, caret right
   * after the lead-in — the guest's "paste your estimate" path (SCRUM-273):
   * the guest may already have typed or pasted the estimate, and that text
   * stays (final web fix round, item 7). A composer already starting with
   * `lead` is left as it is, so a second tap never stacks it. Nothing is sent.
   */
  prependLeadIn: (lead: string) => void;
}

/**
 * Guest mode (SCRUM-273). Absent for members — the composer renders exactly
 * as before.
 */
export interface MessageInputGuestOptions {
  /** Attachments locked (`guest.attachments_enabled` off): the clip/camera
   * keep their place with a lock and open the sign-up gate. */
  attachmentsLocked: boolean;
  /** Open the sign-up gate for a locked attachment control. */
  onLockedAttach: () => void;
  /** Voice for guests — the guest config's voice fields (contract C2). */
  voice: UseTranscribeGuestOptions;
  /** Rendered under the composer surface (privacy notice, remaining hint). */
  footer?: ReactNode;
}

export interface MessageInputProps {
  isStreaming: boolean;
  /**
   * Send the composed message. `attachmentUploadIds` are the ready upload ids
   * threaded into the request body; `attachments` are the display-mapped
   * objects rendered immediately in the user bubble (chat document
   * attachments).
   */
  onSend: (
    message: string,
    attachmentUploadIds: string[],
    attachments: MessageAttachment[],
  ) => void;
  onStop: () => void;
  /**
   * A0: a pre-seeded composer draft, carried from a "Ask the assistant about
   * this" link (a seller / KB article). Prefills the textarea once per distinct
   * seed (it does NOT auto-send — the user reviews / edits then sends). Empty /
   * undefined = no seed (the normal blank composer).
   */
  seedText?: string;
  /** Guest mode (SCRUM-273). */
  guest?: MessageInputGuestOptions;
}

/**
 * The lock on a guest's attach/camera control (SCRUM-273). Decorative: the
 * control keeps its name plus "sign-in required" for assistive tech.
 *
 * `compact` is the design composer's bare 18px «+» (C4, ui-review.md §4.6).
 * At `-bottom-2 -right-2` the 16px badge sat over the glyph's lower-right
 * quarter, 4px from the next control, and read as a rendering glitch. It is
 * now 14px on the glyph's top-right corner, clear of the crossbar and the stem,
 * with its lock scaled to match; the 44px phone buttons keep theirs.
 */
function LockBadge({ testId, compact = false }: { testId: string; compact?: boolean }): ReactElement {
  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={[
        "pointer-events-none absolute grid place-items-center rounded-full",
        "bg-[var(--g-surface)] text-[var(--g-ink-2)] ring-1 ring-[var(--g-rule)]",
        compact ? "size-3.5 -top-1.5 -right-2" : "size-4 bottom-1.5 right-1.5",
      ].join(" ")}
    >
      <Lock {...iconProps("xs")} {...(compact ? { size: 12 } : {})} />
    </span>
  );
}

function MessageInputInner(
  { isStreaming, onSend, onStop, seedText, guest }: MessageInputProps,
  ref: Ref<MessageInputHandle>,
): ReactElement {
  const { t, i18n } = useTranslation("chat");
  /*
   * The imported design's composer (spec §4.7), on the same gate as the frame
   * and the transcript: ≥1200px and light theme. Read here rather than passed
   * in — `ChatRoute` mounts this through a ref (prefill, the guest lead-in,
   * SCRUM-146's quote starter) and threads no layout props at all.
   */
  const design = useAssistantDesign();
  const isGuest = guest !== undefined;
  const attachmentsLocked = guest?.attachmentsLocked === true;
  // The latest guest options for the stable handlers below.
  const guestRef = useRef(guest);
  guestRef.current = guest;
  // A guest's unsent text survives the sign-in round trip (contract:
  // sessionStorage["garden.guest.draft.v1"]).
  const [value, setValue] = useState(() =>
    guest !== undefined ? (readGuestDraft() ?? "") : "",
  );
  // True once the draft has been restored for the CURRENT guest session —
  // immediately when `guest` is already present at mount (the initializer
  // above already read it), false when `guest` arrives later so the mirror
  // effect below restores once before it starts overwriting (fix round 1).
  const draftRestoredRef = useRef(guest !== undefined);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  /** SCRUM-183 — so the mount-time focus fires exactly once per chat. */
  const mountedOnceRef = useRef(false);

  const { drafts, add, remove, clear, readyUploadIds } = useAttachmentUpload({
    guest: isGuest && !attachmentsLocked,
    onSignInRequired: guest?.onLockedAttach,
  });

  /*
   * ---- Voice input -------------------------------------------------------
   * The transcript is APPENDED to whatever is already typed and the caret is
   * left at the end: someone may have typed half a thought before speaking,
   * and replacing their text would destroy it. Nothing is auto-sent — speech
   * recognition is wrong often enough (product names, numbers, RU/KY
   * code-switching) that sending unread would turn each misrecognition into a
   * wasted turn.
   */
  const { config: voiceConfig, isTranscribing, failure, transcribe, clearFailure } =
    useTranscribe(guest !== undefined ? { guest: guest.voice } : {});
  /** Polite announcements for screen readers — transitions only, never the clock. */
  const [voiceAnnouncement, setVoiceAnnouncement] = useState("");

  const maxDurationMs = (voiceConfig?.max_duration_seconds ?? 60) * 1000;

  const handleClip = useCallback(
    (blob: Blob, mimeType: string, durationMs: number): void => {
      setVoiceAnnouncement(t("voice.srTranscribing"));
      void transcribe(blob, mimeType, durationMs, i18n.language).then((result) => {
        if (result === null) return;
        if (result.no_speech) {
          setVoiceAnnouncement(t("voice.noSpeech"));
          return;
        }
        setValue((current) => {
          const joined = current.trim().length > 0 ? `${current.trim()} ${result.text}` : result.text;
          return joined;
        });
        setVoiceAnnouncement(t("voice.srReady"));
        // Refocusing also reopens the soft keyboard — exactly when the user
        // wants to edit, and only then.
        textareaRef.current?.focus();
      });
    },
    [i18n.language, t, transcribe],
  );

  const recorder = useVoiceRecorder({ maxDurationMs, onComplete: handleClip });

  /**
   * Show the microphone only when the browser can record AND the server says
   * voice is on for this user's language. Availability is fetched rather than
   * assumed so an admin disabling Kyrgyz makes the KY microphone disappear
   * instead of merely making it fail after someone has already spoken.
   */
  const voiceAvailable =
    isRecordingSupported() &&
    voiceConfig !== null &&
    voiceConfig.enabled &&
    voiceConfig.languages.includes(i18n.language);

  const voiceActive = recorder.state === "recording" || isTranscribing;

  const startVoice = useCallback((): void => {
    clearFailure();
    setVoiceAnnouncement(t("voice.srStarted"));
    // Dropping focus dismisses the phone keyboard, which would otherwise
    // cover the recording strip the user is trying to watch.
    textareaRef.current?.blur();
    void recorder.start();
  }, [clearFailure, recorder, t]);

  const stopVoice = useCallback((): void => {
    setVoiceAnnouncement(t("voice.srStopped"));
    recorder.stop();
  }, [recorder, t]);

  const cancelVoice = useCallback((): void => {
    setVoiceAnnouncement(t("voice.srStopped"));
    recorder.cancel();
  }, [recorder, t]);

  const openAttach = useCallback((): void => {
    const current = guestRef.current;
    if (current !== undefined && current.attachmentsLocked) {
      current.onLockedAttach();
      return;
    }
    fileInputRef.current?.click();
  }, []);

  const openCamera = useCallback((): void => {
    const current = guestRef.current;
    if (current !== undefined && current.attachmentsLocked) {
      current.onLockedAttach();
      return;
    }
    cameraInputRef.current?.click();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openFilePicker: openAttach,
      prefill: (text: string) => {
        setValue(text);
        const el = textareaRef.current;
        if (el === null) return;
        el.focus();
        // After React commits the new value, so the caret lands at its end.
        requestAnimationFrame(() => el.setSelectionRange(text.length, text.length));
      },
      prependLeadIn: (lead: string) => {
        setValue((current) => (current.startsWith(lead) ? current : lead + current));
        const el = textareaRef.current;
        if (el === null) return;
        el.focus();
        // After React commits the new value: the caret right after the lead-in.
        requestAnimationFrame(() => el.setSelectionRange(lead.length, lead.length));
      },
    }),
    [openAttach],
  );

  // Guest chat: mirror the composer text into the draft (members never write
  // it). `guest` can arrive AFTER mount — the guest config resolves async
  // (SCRUM-275: an existing guest is kept in the chat while that fetch is in
  // flight) — so the lazy `useState` initializer above may have had nothing
  // to read yet. `draftRestoredRef` runs that restore exactly once for this
  // guest session, and this effect skips its own write on that same pass: a
  // plain `if (isGuest) writeGuestDraft(value)` would otherwise write the
  // still-empty `value` the instant `isGuest` turns true, erasing the very
  // draft the restore is about to set (fix round 1).
  useEffect(() => {
    if (!isGuest) return;
    if (!draftRestoredRef.current) {
      draftRestoredRef.current = true;
      const draft = readGuestDraft();
      // Never clobber text the user already typed while guest status was
      // still unknown — only an EMPTY composer adopts the saved draft.
      if (draft !== null && value.length === 0) {
        setValue(draft);
        return;
      }
    }
    writeGuestDraft(value);
  }, [isGuest, value]);

  const autoGrow = (el: HTMLTextAreaElement): void => {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  };

  // A0: prefill the composer from a carried seed ("ask the assistant about this
  // seller / article"). Runs once per distinct non-empty seed; does NOT
  // auto-send — the user reviews / edits, then sends.
  useEffect(() => {
    const seed = seedText?.trim();
    if (seed !== undefined && seed.length > 0) {
      setValue(seed);
      textareaRef.current?.focus();
    }
  }, [seedText]);

  // SCRUM-183 — put the cursor back in the composer when a reply finishes, so
  // the user can keep typing without reaching for the mouse. Fires on the
  // streaming true->false edge (and on mount) rather than on every render.
  //
  // Guarded to fine-pointer devices: on a touch screen, focusing the textarea
  // pops the on-screen keyboard, which would cover the answer the user is
  // trying to read. `matchMedia` is absent in jsdom, hence the optional call.
  const wasStreaming = useRef(isStreaming);
  useEffect(() => {
    const finePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: fine)").matches;
    const justFinished = wasStreaming.current && !isStreaming;
    wasStreaming.current = isStreaming;
    if (finePointer && (justFinished || !mountedOnceRef.current)) {
      mountedOnceRef.current = true;
      textareaRef.current?.focus();
    }
  }, [isStreaming]);

  // Keep the textarea height in sync with its content so a programmatic value
  // change (the seed above) auto-grows too, not just typing.
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
    }
  }, [value]);

  const addFiles = useCallback(
    (files: File[]): void => {
      const slotsLeft = MAX_ATTACHMENTS - drafts.length;
      const toAdd = files.slice(0, Math.max(0, slotsLeft));
      for (const file of toAdd) {
        void add(file);
      }
    },
    [add, drafts.length],
  );

  const submit = (): void => {
    const trimmed = value.trim();
    const hasReady = readyUploadIds.length > 0;
    if ((trimmed.length === 0 && !hasReady) || isStreaming) return;
    // Display objects for the user bubble: only the READY drafts (those with a
    // settled upload id) render — a still-uploading draft is dropped on send.
    const displayAttachments = drafts
      .filter((d) => d.state === "ready" && d.uploadId !== null)
      .map(draftToDisplay);
    onSend(trimmed, readyUploadIds, displayAttachments);
    setValue("");
    clear();
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend =
    (value.trim().length > 0 || readyUploadIds.length > 0) && !isStreaming;

  /** Drag-drop / paste — progressive enhancement; buttons remain primary. */
  const { getRootProps, isDragActive } = useDropzone({
    // A locked guest's drop opens the gate. Disabling the dropzone instead
    // would let the browser open the dropped file in this tab.
    onDrop: (accepted: File[]) => {
      const current = guestRef.current;
      if (current !== undefined && current.attachmentsLocked) {
        current.onLockedAttach();
        return;
      }
      addFiles(accepted);
    },
    accept: Object.fromEntries(
      ACCEPTED_TYPES.map((mimeType) => [mimeType, []]),
    ),
    noClick: true, // buttons handle click; dropzone only handles drag+paste
    noKeyboard: true,
    // Fix round 1: a DISABLED dropzone returns null drag handlers, so the
    // drop event is never intercepted and the browser opens the file in this
    // tab instead — for a guest that is a dead end a locked composer cannot
    // recover from. A member still can't drop mid-stream (unchanged); a guest
    // can, and the SAME onDrop above routes it to the gate exactly as if idle.
    disabled: isGuest ? false : isStreaming,
  });

  return (
    <div
      {...getRootProps()}
      data-testid="chat-composer"
      className={
        design
          ? // Spec §4.7: `12px 32px 20px`, and no top rule — the design's
            // composer sits ON the lattice ground rather than on a bar.
            "px-8 pb-5 pt-3"
          : "border-t border-[var(--g-rule)] bg-[var(--chat-ground)] px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
      }
    >
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="chat-file-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          addFiles(files);
          // Reset so the same file can be re-selected after removal.
          e.target.value = "";
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="chat-camera-input"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          addFiles(files);
          e.target.value = "";
        }}
      />

      {/*
       * ONE surface, not three.
       *
       * The clip and camera buttons used to be standalone bordered boxes to the
       * LEFT of a separate bordered field, so the composer read as three
       * unrelated controls and the field itself lost a third of its width on a
       * 390px screen — which is also what clipped the placeholder: the textarea
       * was locked to one line's height while the placeholder wrapped to two,
       * and the second line rendered outside the box.
       *
       * Now the text sits across the full width with its own tool row beneath
       * it, inside a single bordered surface that owns the focus ring. Same
       * controls, same handlers, same test ids — the field just gets its width
       * back and the group finally reads as one thing.
       */}
      {design ? (
        /*
         * The design's composer (spec §4.7): ONE row — attach, field, hint,
         * send — inside a 1.5px `--gd-focus-ring` frame at radius 18.
         *
         * Every handler below is the incumbent's, unchanged, and so is every
         * testid: this is a restyle, not a second composer. Two deliberate
         * differences from the row above it:
         *
         *   • No camera control. The design draws one attach affordance, and
         *     on the ≥1200px viewports this branch renders on, `capture=
         *     "environment"` is ignored — the button degenerates into a
         *     second, image-only file picker. Mobile keeps today's composer
         *     entirely, camera included.
         *   • The Enter hint is always shown rather than `lg:` gated, because
         *     this branch only exists above 1200px in the first place.
         */
        <div className="mx-auto flex w-full max-w-[780px] flex-col gap-[10px]">
          {drafts.length > 0 && (
            <AttachmentChips drafts={drafts} onRemove={remove} />
          )}

          {/*
           * One text line, one centre line (C3, ui-review.md §4.6). Every
           * control sits on a line 22px above the row's bottom, the middle of
           * the 44px field: the field's own text (`py-[11px]`), the 18px «+»
           * and mic (`mb-[13px]`), the hint (`mb-[11px]`) and the 40px send
           * button (`mb-0.5`). They used to sit 5–8px low. `items-end` stays,
           * so a multi-line draft still grows upward with the controls on its
           * last line.
           */}
          <div
            className="flex items-end gap-3 rounded-[18px] py-2 pl-5 pr-2"
            style={{
              backgroundColor: "var(--gd-surface)",
              border: `1.5px solid ${
                isDragActive ? "var(--gd-primary)" : "var(--gd-focus-ring)"
              }`,
            }}
          >
            {voiceActive ? (
              <VoiceRecordingStrip
                elapsedMs={recorder.elapsedMs}
                level={recorder.level}
                maxDurationMs={maxDurationMs}
                isTranscribing={isTranscribing}
                onStop={stopVoice}
                onCancel={cancelVoice}
              />
            ) : (
              <>
            <button
              type="button"
              onClick={openAttach}
              disabled={isStreaming || drafts.length >= MAX_ATTACHMENTS}
              aria-label={attachmentsLocked ? t("guest.locked.attachAria") : t("cta.uploadAria")}
              aria-haspopup={attachmentsLocked ? "dialog" : undefined}
              title={attachmentsLocked ? t("guest.locked.attachAria") : t("cta.upload")}
              data-testid="chat-attach"
              /* `mr-1` only under the lock: it gives the badge room before
                 the next control (C4); an open «+» keeps the design's gap. */
              className={
                "relative gd-hit-44 mb-[13px] grid size-[18px] shrink-0 place-items-center rounded-[6px] transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] motion-reduce:transition-none" +
                (attachmentsLocked ? " mr-1" : "")
              }
              style={{ color: "var(--gd-neutral)" }}
            >
              <Plus size={18} strokeWidth={2} aria-hidden="true" />
              {attachmentsLocked ? <LockBadge testId="chat-attach-lock" compact /> : null}
            </button>

            {voiceAvailable && (
              <button
                type="button"
                onClick={startVoice}
                disabled={isStreaming}
                aria-label={t("voice.startAria")}
                title={t("voice.start")}
                data-testid="chat-mic-design"
                /* Sized and hit-extended exactly like the attach button
                   beside it — the design has no microphone, so this borrows
                   its neighbour's anatomy rather than inventing one. */
                className="gd-hit-44 mb-[13px] grid size-[18px] shrink-0 place-items-center rounded-[6px] transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] motion-reduce:transition-none"
                style={{ color: "var(--gd-neutral)" }}
              >
                <Icon name="mic" size={18} />
              </button>
            )}

            <label htmlFor="chat-input" className="sr-only">
              {t("input.ariaLabel")}
            </label>
            <textarea
              id="chat-input"
              ref={textareaRef}
              rows={1}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                autoGrow(e.target);
              }}
              onKeyDown={onKeyDown}
              placeholder={t("input.placeholder")}
              aria-label={t("input.ariaLabel")}
              disabled={isStreaming}
              data-testid="chat-input"
              /* 44px, not the design's 40: a text field is a pointer target
                 and cannot take the `.gd-hit-44` overlay — an invisible box
                 over a field sits between the pointer and the caret and
                 breaks click-to-position (design-world.css). So this one
                 clears the floor by being the size it needs, which costs the
                 composer row 4px of height and nothing else. `py-[11px]`
                 centres one line of text in it (C3). The placeholder takes
                 `--gd-ink-3` (5.32:1 on --gd-surface); left to the default,
                 ink at 50%, it measured 3.26:1 (C5). */
              className="block max-h-[140px] min-h-[44px] w-full flex-1 resize-none bg-transparent py-[11px] text-[16px] leading-[1.45] outline-none placeholder:text-[color:var(--gd-ink-3)] disabled:opacity-60"
              style={{
                color: "var(--gd-ink)",
                fontFamily: "var(--gd-text)",
              }}
            />

            <span
              aria-hidden="true"
              data-testid="composer-hint"
              className="mb-[11px] hidden select-none items-center gap-1 text-[12.5px] sm:inline-flex"
              style={{ color: "var(--gd-ink-3)" }}
            >
              <kbd
                className="rounded-[5px] px-1.5 py-0.5 text-[11.5px] font-medium"
                style={{
                  border: "1px solid var(--gd-rule-2)",
                  fontFamily: "var(--gd-mono)",
                }}
              >
                Enter
              </kbd>
              {t("input.enterHint")}
            </span>

            {isStreaming ? (
              <StopButton onStop={onStop} />
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canSend}
                aria-label={t("cta.sendAria")}
                title={t("cta.send")}
                data-testid="chat-send"
                /* The design draws 40×40 — under the 44px floor, so the hit
                   area is extended by the shared `.gd-hit-44` overlay rather
                   than by resizing a button the design already sized. */
                className="gd-hit-44 mb-0.5 grid size-10 shrink-0 place-items-center rounded-[12px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] focus-visible:ring-offset-2 motion-reduce:transition-none"
                style={{
                  backgroundColor: canSend
                    ? "var(--gd-primary)"
                    : "var(--gd-neutral)",
                  color: "var(--gd-on-primary)",
                }}
                onMouseEnter={(e) => {
                  if (canSend) {
                    e.currentTarget.style.backgroundColor =
                      "var(--gd-primary-hover)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = canSend
                    ? "var(--gd-primary)"
                    : "var(--gd-neutral)";
                }}
              >
                <Icon name="arrowUp" size={18} stroke={2.2} />
              </button>
            )}
              </>
            )}
          </div>

          {isDragActive ? (
            <p
              className="text-center text-[13.5px] font-medium"
              style={{ color: "var(--gd-primary)" }}
              data-testid="chat-drop-hint"
            >
              {attachmentsLocked ? t("guest.locked.dropHint") : t("attach.dropHint")}
            </p>
          ) : null}

          {guest?.footer}
        </div>
      ) : (
        <div className="mx-auto w-full max-w-[760px]">
          <div
            className={[
              "rounded-[var(--g-radius-lg)] border bg-[var(--g-surface)] transition-[border-color,box-shadow] duration-150",
              "focus-within:border-[var(--g-primary)] focus-within:shadow-[0_0_0_3px_var(--g-primary-soft)]",
              isDragActive
                ? "border-[var(--g-primary)] shadow-[0_0_0_3px_var(--g-primary-soft)]"
                : "border-[var(--g-rule)]",
            ].join(" ")}
          >
            {/* Attachment chips live INSIDE the composer surface — they belong to
              the message being written, not to the page. */}
            {drafts.length > 0 && (
              <div className="border-b border-[var(--g-hairline)] px-3 pb-2.5 pt-3">
                <AttachmentChips drafts={drafts} onRemove={remove} />
              </div>
            )}

            <label htmlFor="chat-input" className="sr-only">
              {t("input.ariaLabel")}
            </label>
            <textarea
              id="chat-input"
              ref={textareaRef}
              rows={1}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                autoGrow(e.target);
              }}
              onKeyDown={onKeyDown}
              placeholder={t("input.placeholder")}
              aria-label={t("input.ariaLabel")}
              disabled={isStreaming}
              data-testid="chat-input"
              className="block max-h-[140px] min-h-[46px] w-full resize-none bg-transparent px-4 pt-3.5 text-[16px] leading-[1.45] text-foreground outline-none placeholder:text-[var(--g-ink-2)] disabled:opacity-60"
            />

            {voiceActive ? (
              <VoiceRecordingStrip
                elapsedMs={recorder.elapsedMs}
                level={recorder.level}
                maxDurationMs={maxDurationMs}
                isTranscribing={isTranscribing}
                onStop={stopVoice}
                onCancel={cancelVoice}
              />
            ) : (
            /* Tool row. Left: what you can attach. Right: how to send. */
            <div className="flex items-center gap-1 px-2 pb-2 pt-1">
              <button
                type="button"
                onClick={openAttach}
                disabled={isStreaming || drafts.length >= MAX_ATTACHMENTS}
                aria-label={attachmentsLocked ? t("guest.locked.attachAria") : t("cta.uploadAria")}
                aria-haspopup={attachmentsLocked ? "dialog" : undefined}
                title={attachmentsLocked ? t("guest.locked.attachAria") : t("cta.upload")}
                data-testid="chat-attach"
                className="relative grid size-11 shrink-0 place-items-center rounded-[10px] text-[var(--g-ink-3)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-primary active:scale-95 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none"
              >
                <Icon name="clip" size={19} />
                {attachmentsLocked ? <LockBadge testId="chat-attach-lock" /> : null}
              </button>

              <button
                type="button"
                onClick={openCamera}
                disabled={isStreaming || drafts.length >= MAX_ATTACHMENTS}
                aria-label={attachmentsLocked ? t("guest.locked.cameraAria") : t("cta.cameraAria")}
                aria-haspopup={attachmentsLocked ? "dialog" : undefined}
                title={attachmentsLocked ? t("guest.locked.cameraAria") : t("cta.camera")}
                data-testid="chat-camera"
                className="relative grid size-11 shrink-0 place-items-center rounded-[10px] text-[var(--g-ink-3)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-primary active:scale-95 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none"
              >
                <Icon name="camera" size={19} />
                {attachmentsLocked ? <LockBadge testId="chat-camera-lock" /> : null}
              </button>

              {/* Third in the left group: clip, camera, mic — the group is
                "ways to put something in". Same anatomy as its neighbours;
                the prototype has no microphone, so nothing here is invented. */}
              {voiceAvailable && (
                <button
                  type="button"
                  onClick={startVoice}
                  disabled={isStreaming}
                  aria-label={t("voice.startAria")}
                  title={t("voice.start")}
                  data-testid="chat-mic"
                  className="grid size-11 shrink-0 place-items-center rounded-[10px] text-[var(--g-ink-3)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-primary active:scale-95 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none"
                >
                  <Icon name="mic" size={19} />
                </button>
              )}

              <div className="flex-1" />

              {/* The keyboard contract, stated rather than assumed. Fine-pointer
                only: there is no Enter key worth naming on a phone, and no
                hover to reveal a tooltip either. */}
              <span
                aria-hidden="true"
                className="mr-1.5 hidden select-none items-center gap-1 text-[12.5px] text-[var(--g-ink-2)] lg:inline-flex"
              >
                <kbd className="g-data rounded-[5px] border border-[var(--g-hairline)] bg-[var(--g-sunken)] px-1.5 py-0.5 text-[11.5px] font-medium">
                  Enter
                </kbd>
                {t("input.enterHint")}
              </span>

              {isStreaming ? (
                <StopButton onStop={onStop} />
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSend}
                  aria-label={t("cta.sendAria")}
                  title={t("cta.send")}
                  data-testid="chat-send"
                  className={[
                    "grid size-11 shrink-0 place-items-center rounded-[10px]",
                    "transition-[background-color,color,transform] duration-150",
                    "active:scale-95 focus-visible:outline-none focus-visible:ring-2",
                    "focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2",
                    "focus-visible:ring-offset-[var(--g-surface)] motion-reduce:transition-none",
                    canSend
                      ? "bg-primary text-[var(--g-on-primary)] hover:brightness-110"
                      : "bg-[var(--g-sunken)] text-[var(--g-ink-3)]",
                  ].join(" ")}
                >
                  <Icon name="arrowUp" size={18} stroke={2.2} />
                </button>
              )}
            </div>
            )}

            {/* Transitions only — a screen reader reading a ticking clock is
              unusable, so the visible timer is aria-hidden and this announces
              started / stopped / transcribing / ready. */}
            <p aria-live="polite" className="sr-only" data-testid="voice-live">
              {voiceAnnouncement}
            </p>

            {/* Voice failures render inline under the composer, never as a red
              toast: every one of them is recoverable by recording again or
              typing instead. */}
            {failure !== null && (
              <p
                className="px-4 pb-2 text-[13.5px] text-[var(--g-ink-2)]"
                data-testid="voice-error"
              >
                {failure.kind === "rateLimited"
                  ? t("voice.rateLimited", { seconds: failure.retryAfterSeconds ?? 5 })
                  : failure.kind === "quotaExhausted"
                    ? t("voice.quotaExhausted")
                    : failure.kind === "tooLong"
                      ? t("voice.tooLong")
                      : failure.kind === "unsupported"
                        ? t("voice.unsupported")
                        : t("voice.error")}
              </p>
            )}

            {/* Permission denial keeps the button visible and explains how to
              undo it. Hiding the control would read as "the feature is
              broken" rather than "your browser is blocking it". */}
            {recorder.state === "denied" && (
              <p
                className="px-4 pb-2 text-[13.5px] text-[var(--g-ink-2)]"
                data-testid="voice-permission"
              >
                {t("voice.permissionHelp")}
              </p>
            )}
          </div>

          {guest?.footer}

          {/* Drop target feedback. A tinted page background said "something is
            draggable somewhere"; this names the target and what it accepts. */}
          {isDragActive ? (
            <p
              className="mt-2 text-center text-[13.5px] font-medium text-primary"
              data-testid="chat-drop-hint"
            >
              {attachmentsLocked ? t("guest.locked.dropHint") : t("attach.dropHint")}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  MessageInputInner,
);
MessageInput.displayName = "MessageInput";

export default MessageInput;
